/**
 * Chart Color Palette
 *
 * All series colors are theme-aware and resolved from CSS variables at render time.
 * Use `getThemeAwareChartColors()` inside components, keyed by `ThemeColorName`.
 */

/**
 * Reads a CSS variable value from the document root.
 * Returns the fallback if the variable is not set.
 */
const getCSSVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

/**
 * Returns theme-aware chart colors by reading CSS variables.
 * Call this inside a React component (after render) so CSS variables are resolved.
 * Wrap in `useMemo(() => getThemeAwareChartColors(), [isDarkMode])` to recompute on theme change.
 */
export const getThemeAwareChartColors = () => ({
  // UI element colors
  background: getCSSVar('--card', '#fff'),
  foreground: getCSSVar('--foreground', '#1E293B'),
  axisLabel: getCSSVar('--muted-foreground', '#666'),
  gridLine: getCSSVar('--border', '#e0e0e0'),
  tooltip: getCSSVar('--foreground', '#333'),
  tooltipBg: getCSSVar('--popover', '#fff'),
  cardBorder: getCSSVar('--card-border', '#E2E8F0'),

  // Semantic series colors — adapt to light/dark via CSS variables
  success:  getCSSVar('--chart-1', '#004F69'),
  error:    getCSSVar('--chart-3', '#D13C59'),
  neutral:  getCSSVar('--chart-neutral', '#64748B'),
  warning:  getCSSVar('--chart-3',  '#06B6D4'),
  total:    getCSSVar('--chart-6',  '#7C3AED'),
  info:     getCSSVar('--chart-7',  '#0EA5E9'),
  active:   getCSSVar('--chart-8',  '#10B981'),
  inactive: getCSSVar('--chart-neutral', '#64748B'),
  pending:  getCSSVar('--chart-4',  '#2563EB'),
  thresholdHigh:   getCSSVar('--chart-negative', '#D13C59'),
  thresholdMedium: getCSSVar('--chart-3', '#06B6D4'),
  thresholdLow:    getCSSVar('--chart-positive', '#009B3A'),

  // Numbered palette slots
  chart1:  getCSSVar('--chart-1',  '#004F69'),
  chart2:  getCSSVar('--chart-2',  '#7C3AED'),
  chart3:  getCSSVar('--chart-3',  '#06B6D4'),
  chart4:  getCSSVar('--chart-4',  '#2563EB'),
  chart5:  getCSSVar('--chart-5',  '#10B981'),
  chart6:  getCSSVar('--chart-6',  '#7C3AED'),
  chart7:  getCSSVar('--chart-7',  '#0EA5E9'),
  chart8:  getCSSVar('--chart-8',  '#10B981'),
  chart9:  getCSSVar('--chart-9',  '#F43F5E'),
  chart10: getCSSVar('--chart-10', '#8B5CF6'),
});

/** Semantic color name — any key accepted by `resolveColor()` */
export type ThemeColorName = keyof ReturnType<typeof getThemeAwareChartColors>;

export const ChartColors = {
  // Primary Colors
  success: '#2A9D8F',      // Green - for success metrics, positive outcomes
  warning: '#004F69',      // Dark Teal - for warning states, attempts, activity
  error: '#D13C59',        // Pink/Red - for errors, failures, critical alerts
  total: '#0EA5E9',        // Sky Blue - for totals, rates, percentages
  
  // Extended Palette (for additional series)
  info: '#0EA5E9',         // Sky Blue - for informational metrics
  neutral: '#64748B',      // Gray - for neutral/baseline metrics
  
  // State Colors
  active: '#10B981',       // Bright Green - for active states
  inactive: '#6B7280',     // Medium Gray - for inactive states
  pending: '#F59E0B',      // Orange - for pending/in-progress states
  
  // Chart UI Elements
  gridLine: '#e0e0e0',     // Grid lines
  axisLabel: '#666',       // Axis labels and text
  tooltip: '#333',         // Tooltip text
  background: '#fff',      // Chart background
  
  // Thresholds
  thresholdHigh: '#EF4444',    // Red - high threshold
  thresholdMedium: '#F59E0B',  // Orange - medium threshold  
  thresholdLow: '#10B981',     // Green - low threshold
} as const;

/**
 * Chart Color Sets
 * 
 * Pre-defined color combinations for common chart patterns
 */
export const ChartColorSets = {
  // Success/Failure Pattern
  successFailure: {
    success: ChartColors.success,
    failure: ChartColors.error,
    rate: ChartColors.total,
  },
  
  // Success/Attempt Pattern (most common)
  successAttempt: {
    success: ChartColors.success,
    attempt: ChartColors.warning,
    rate: ChartColors.total,
  },
  
  // Traffic Light Pattern
  trafficLight: {
    good: ChartColors.success,
    warning: ChartColors.warning,
    critical: ChartColors.error,
  },
  
  // State Pattern
  state: {
    active: ChartColors.active,
    inactive: ChartColors.inactive,
    pending: ChartColors.pending,
  },
} as const;

/**
 * Get color by semantic name
 * 
 * @param name - Semantic color name ('success', 'error', 'warning', 'total')
 * @returns Hex color code
 */
export const getChartColor = (name: keyof typeof ChartColors): string => {
  return ChartColors[name];
};

/**
 * Type for color names (for type safety)
 */
export type ChartColorName = keyof typeof ChartColors;
export type ChartColorSet = keyof typeof ChartColorSets;

export default ChartColors;